"use client";

import { useLocale } from "@/i18n/LocaleProvider";
import { formatAmount } from "@/lib/decimal";
import { cn } from "./cn";

export type MeasureUnit = "kg" | "m³" | "kg/m³";

export interface MeasureProps {
  /** Decimal value as sent by the backend (string keeps the exact scale) or a JS number. */
  value: string | number | null | undefined;
  /** Canonical unit — rendered in the current locale ("kg" → «кг» in Russian). */
  unit: MeasureUnit;
  /** Shown when the value is missing / not a number. */
  placeholder?: string;
  className?: string;
}

/**
 * Renders a decimal measurement rounded to at most two places and without
 * misleading trailing zeros, so "123.000" shows as "123 kg" (not "123 000")
 * and "123.456" as "123.46 kg". The integer part is locale-grouped.
 */
export function Measure({ value, unit, placeholder = "—", className }: MeasureProps) {
  const { locale, dict } = useLocale();
  const unitLabel = { kg: dict.units.kg, "m³": dict.units.m3, "kg/m³": dict.units.kgM3 }[unit];
  const text = formatAmount(value, locale);

  if (text == null) {
    return <span className={className}>{placeholder}</span>;
  }

  return (
    <span className={cn("whitespace-nowrap tabular-nums text-foreground", className)}>
      {text}
      &nbsp;{unitLabel}
    </span>
  );
}
